import { memo, useCallback, useEffect, useRef, useState } from "react";
import { formatTimecode } from "@infoflyt/shared/timecode";
import { useSession } from "../state/SessionProvider";
import { newId } from "../state/db";
import { MARKER_COLORS, MARKER_PALETTE } from "../theme/tokens";
import { confirm as hapticConfirm, tap as hapticTap } from "../components/haptics";
import { useExitDelay } from "../components/useExitDelay";
import type { LogButton } from "../state/types";

/**
 * Terskelen er 800 ms, ikke iOS' 500. En finger som blir liggende et
 * halvsekund under en hendelse er vanlig. Se docs/13-design.md.
 */
const HOLD_MS = 800;
/** Maa foelge --t-enter i surfaces.css: holder noden montert til utgangen er ferdig. */
const TOAST_UT_MS = 220;
const TOAST_MS = 4000;
/** Etiketten har plass til «INNKLIPPSBILDE», som er fjorten tegn. */
/*
 * Hvor lang en knappetikett kan vaere.
 *
 * Fjorten var for kort: brukeren 02.11.2026 skrev «Saa var eventyret
 * over» og fikk «Saa var eventyr». Etiketten fanges paa markoeren og
 * staar i loggen — den ER posten for en markoer uten tekst — saa den maa
 * kunne si hva den handler om.
 *
 * Tjueaatte og ikke mer: knappen skal kunne LESES i et blikk med en
 * tommel, og tre linjer i kortet er grensen for det.
 */
const LABEL_MAX = 28;
/** Går man over det som får plass på to kolonner, legges det om til tre. */
const THREE_COL_FROM = 9;

/* Memo: klokka gjengir skallet ti ganger i sekundet, og uten dette
   bygges hele denne flata paa nytt hver gang — inkludert ref-ene paa
   hvert kort, som React da kobler fra og paa igjen. */
export const HurtigloggScreen = memo(function HurtigloggScreen({
  editMode, onEditModeChange,
}: {
  editMode: boolean;
  onEditModeChange(v: boolean): void;
}): JSX.Element {
  const { buttons, setButtons, addMarker, withdrawEntry, spec } = useSession();

  const [toast, setToast] = useState<{ id: string; text: string } | null>(null);
  const [pressed, setPressed] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ from: number; to: number; dx: number; dy: number } | null>(null);

  const hold = useRef<number | null>(null);
  const toastTimer = useRef<number | null>(null);
  /*
   * Toasten skal kunne gaa ut.
   *
   * Innholdet maa overleve at `toast` blir null, ellers er det ingenting aa
   * tegne mens den toner bort. Byttes den mot en ny markoer gaar den ikke ut
   * i det hele tatt — `key` under gjoer at noden byttes og inngangen spilles
   * paa nytt, som er med vilje: hver markoer skal melde seg selv.
   */
  const sisteToast = useRef<{ id: string; text: string } | null>(null);
  if (toast !== null) sisteToast.current = toast;
  const { mounted: toastVises, shown: toastInne } = useExitDelay(toast !== null, TOAST_UT_MS);
  /* Noekkelen er indeksen, ikke knappens id. Det holder fordi React kobler
     fra alle ref-er foer den kobler paa igjen, men det hviler paa en
     rekkefoelge som ikke er lovet. Se PagesPane, som gjoer det riktig. */
  const cards = useRef<Map<number, HTMLDivElement>>(new Map());
  const origin = useRef<{ x: number; y: number } | null>(null);
  /** Pekeren som holder inne, og lytteren som avslutter holdet. */
  const holder = useRef<{ id: number; naar: number; slipp: () => void } | null>(null);

  useEffect(() => () => {
    if (hold.current !== null) window.clearTimeout(hold.current);
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    // Skrus Hurtiglogg av i Sider mens en finger staar nede, ble lytteren
    // liggende paa dokumentet med hele renderen bak seg.
    holder.current?.slipp();
  }, []);

  /**
   * Markøren settes på pointerdown, ikke på pointerup.
   *
   * Det er riktig uansett — tidskode fanges ved knappetrykk, og nedslaget
   * *er* trykket. Konsekvensen er at et langtrykk allerede har satt markøren
   * før dansen starter, så den utilsiktede gesten koster ingenting.
   */
  const press = useCallback((button: LogButton, pointerId: number, x: number, y: number): void => {
    /*
     * Ett trykk om gangen. To fingre paa hver sin knapp skrev to markoerer,
     * og loggen kan bare legges til — den ene maatte fjernes for haand.
     *
     * Men et hold som aldri fikk slippe skal ikke
     * laase knappene for godt. Leverer iOS hverken pointerup eller
     * pointercancel (systemgest, eller appen sendt i bakgrunnen midt i et
     * trykk), overtar neste trykk naar det forrige er eldre enn holdet.
     */
    if (holder.current !== null) {
      if (Date.now() - holder.current.naar < HOLD_MS + 400) return;
      holder.current.slipp();
    }
    const marker = addMarker(button);
    if (marker === null) return;
    setPressed(button.id);
    hapticTap();

    setToast({ id: marker.id, text: `Markør satt ${formatTimecode(marker.frames, spec)}` });
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), TOAST_MS);

    /*
     * Trekker markoeren tilbake. Den ble aldri ment.
     *
     * Brukes av BAADE sveipen og holdet: et nedslag som fortsetter i noe
     * annet enn et trykk er ikke et trykk. Posten trekkes tilbake i stedet
     * for aa slettes — den skal ikke etterlate seg et spor i loggen.
     */
    const angre = (): void => {
      withdrawEntry(marker.id);
      setToast((t) => (t !== null && t.id === marker.id ? null : t));
      if (toastTimer.current !== null) {
        window.clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    };

    if (hold.current !== null) window.clearTimeout(hold.current);
    hold.current = window.setTimeout(() => {
      /*
       * HOLDET SKAL IKKE LOGGE NOE.
       *
       * Markoeren settes paa nedslaget fordi tidskoden fanges ved trykket,
       * og da har ogsaa et hold satt én. Foer sto den igjen med «Angre», og
       * regi maatte rydde etter seg hver gang hen aapnet knappeoppsettet.
       * Det er den samme feilen som sveipen hadde: et nedslag som ikke
       * endte i et trykk, er ikke et trykk.
       */
      angre();
      onEditModeChange(true);
      setPressed(null);
      hapticConfirm();
    }, HOLD_MS);

    /*
     * Slipp maa ogsaa leses fra dokumentet.
     *
     * Pager laaser aksen ved seks piksler og tar pekeren med
     * setPointerCapture. Fra da av gaar hendelsene til .pager, og kortet ser
     * hverken pointerup eller pointercancel — saa et sidebytte som startet
     * paa et kort lot holdet leve, og slettemodus slo inn et halvt sekund
     * senere paa en helt annen flate.
     *
     * Bare pekerens egen slipp teller. Uten det stengte en tilfeldig andre
     * finger et holdet som var ment.
     */
    const slipp = (e?: PointerEvent): void => {
      if (e !== undefined && e.pointerId !== pointerId) return;
      document.removeEventListener("pointerup", slipp);
      document.removeEventListener("pointercancel", slipp);
      document.removeEventListener("pointermove", beveger);
      holder.current = null;
      if (hold.current !== null) { window.clearTimeout(hold.current); hold.current = null; }
      setPressed(null);
    };

    /*
     * Blir trykket en sveip, var det ingen markoer.
     *
     * Markoeren settes paa nedslaget fordi tidskoden fanges ved trykket, og
     * nedslaget *er* trykket. Men et nedslag som fortsetter i en bevegelse er
     * blaing mellom sidene — da har regi ikke ment aa sette noe, og loggen
     * satt igjen med en markoer for hver knapp fingeren strøk over.
     *
     * Posten trekkes tilbake i stedet for aa slettes: den ble aldri ment, saa
     * den skal ikke etterlate seg et spor i loggen.
     */
    const beveger = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      if (Math.abs(e.clientX - x) < 12 && Math.abs(e.clientY - y) < 12) return;
      angre();
      slipp();
    };

    holder.current = { id: pointerId, naar: Date.now(), slipp };
    document.addEventListener("pointerup", slipp);
    document.addEventListener("pointercancel", slipp);
    document.addEventListener("pointermove", beveger);
  }, [addMarker, withdrawEntry, spec, onEditModeChange]);

  const release = useCallback((e?: React.PointerEvent): void => {
    // Bare pekerens egen slipp. Uten dette stengte release() holdet uten
    // hensyn til hvilken finger som slapp, og omgikk sjekken i slipp().
    if (e !== undefined && holder.current !== null && holder.current.id !== e.pointerId) return;
    holder.current?.slipp();
    if (hold.current !== null) { window.clearTimeout(hold.current); hold.current = null; }
    setPressed(null);
  }, []);

  const undo = useCallback((): void => {
    if (toast === null) return;
    withdrawEntry(toast.id);
    setToast(null);
  }, [toast, withdrawEntry]);

  const addNew = useCallback((): void => {
    const used = new Set(buttons.map((b) => b.color));
    const fresh = MARKER_PALETTE.find((c) => !used.has(c));
    // Er alle seks i bruk, gjenbrukes de i rekkefølge. Grå er siste utvei,
    // og er alltid definert — oppslag med regnet indeks er det ikke.
    const color: string =
      fresh ?? MARKER_PALETTE[buttons.length % MARKER_PALETTE.length] ?? MARKER_COLORS.gra;
    setButtons([...buttons, { id: newId(), label: "NY KNAPP", color, sortOrder: buttons.length }]);
    onEditModeChange(true);
  }, [buttons, setButtons, onEditModeChange]);

  // ---- flytting: dra en knapp, de andre flyter unna -----------------------

  const dragStart = (e: React.PointerEvent<HTMLDivElement>, i: number): void => {
    if (!editMode) return;
    if ((e.target as HTMLElement).tagName === "INPUT") return;
    const box = cards.current.get(i)?.getBoundingClientRect();
    if (box === undefined) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ingen aktiv peker */ }
    origin.current = { x: e.clientX, y: e.clientY };
    setDrag({ from: i, to: i, dx: 0, dy: 0 });
  };

  const dragMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const o = origin.current;
    if (o === null || drag === null) return;
    const dx = e.clientX - o.x;
    const dy = e.clientY - o.y;
    const held = cards.current.get(drag.from)?.getBoundingClientRect();
    if (held === undefined) return;
    const cx = held.left + held.width / 2 + dx;
    const cy = held.top + held.height / 2 + dy;

    let to = drag.from;
    let best = Infinity;
    cards.current.forEach((el, i) => {
      const b = el.getBoundingClientRect();
      const d = (b.left + b.width / 2 - cx) ** 2 + (b.top + b.height / 2 - cy) ** 2;
      if (d < best) { best = d; to = i; }
    });
    setDrag({ from: drag.from, to, dx, dy });
  };

  const dragEnd = (): void => {
    origin.current = null;
    if (drag === null) return;
    if (drag.from !== drag.to) {
      const next = buttons.slice();
      const [moved] = next.splice(drag.from, 1);
      if (moved !== undefined) next.splice(drag.to, 0, moved);
      setButtons(next);
    }
    setDrag(null);
  };

  /** Hvor et kort må flytte seg for å gi plass til det som dras. */
  const shift = (i: number): string => {
    if (drag === null || i === drag.from) return "";
    const { from, to } = drag;
    let slot = i;
    if (from < to && i > from && i <= to) slot = i - 1;
    else if (to < from && i >= to && i < from) slot = i + 1;
    if (slot === i) return "";
    const a = cards.current.get(i)?.getBoundingClientRect();
    const b = cards.current.get(slot)?.getBoundingClientRect();
    if (a === undefined || b === undefined) return "";
    return `translate3d(${b.left - a.left}px, ${b.top - a.top}px, 0)`;
  };

  if (buttons.length === 0) {
    // Den eneste veien inn i oppsettet for en ny bruker, siden gesten ikke er
    // synlig. Den tomme flaten må invitere, ikke bare være tom.
    return (
      <div className="surface" style={{ height: "100%", display: "flex" }}>
        <div className="empty">
          <button type="button" className="empty__add" onClick={addNew} aria-label="Lag hurtigknapp">
            +
          </button>
          <p className="empty__text">
            Lag knappene du trenger.
            <br />
            Ett trykk setter markør.
          </p>
        </div>
      </div>
    );
  }

  const cols = buttons.length >= THREE_COL_FROM ? 3 : 2;

  return (
    <div className="surface" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div className={`grid grid--cols-${cols}`}>
        {buttons.map((b, i) => {
          const held = drag?.from === i;
          return (
            <div
              key={b.id}
              ref={(el) => {
                if (el === null) cards.current.delete(i);
                else cards.current.set(i, el);
              }}
              className={[
                "card",
                editMode && !held ? "card--dancing" : "",
                held ? "card--held" : "",
                pressed === b.id ? "card--pressed" : "",
              ].filter(Boolean).join(" ")}
              style={{
                transform: held
                  ? `translate3d(${drag.dx}px, ${drag.dy}px, 0) scale(1.05)`
                  : shift(i),
                touchAction: editMode ? "none" : "pan-y",
              }}
              onPointerDown={(e) => (editMode ? dragStart(e, i) : press(b, e.pointerId, e.clientX, e.clientY))}
              onPointerMove={editMode ? dragMove : undefined}
              onPointerUp={editMode ? dragEnd : release}
              onPointerCancel={editMode ? dragEnd : release}
              role={editMode ? undefined : "button"}
              aria-label={editMode ? undefined : `Sett markør ${b.label}`}
            >
              <span className="entry__stripe" style={{ background: b.color, borderRadius: "2px 0 0 2px" }} />

              {editMode ? (
                <button
                  type="button"
                  className="card__remove"
                  onClick={() => setButtons(buttons.filter((x) => x.id !== b.id))}
                  aria-label={`Fjern ${b.label}`}
                >
                  <span />
                </button>
              ) : null}

              {editMode ? (
                <input
                  className="card__rename"
                  value={b.label}
                  maxLength={LABEL_MAX}
                  spellCheck={false}
                  onPointerDown={(e) => e.stopPropagation()}
                  onChange={(e) =>
                    setButtons(buttons.map((x) => (x.id === b.id ? { ...x, label: e.target.value } : x)))
                  }
                />
              ) : (
                <span className="mono entry__label">{b.label}</span>
              )}
            </div>
          );
        })}

        {editMode ? (
          <button type="button" className="card card--add" onClick={addNew} aria-label="Ny hurtigknapp">
            +
          </button>
        ) : null}
      </div>

      {toastVises && sisteToast.current !== null ? (
        /* Egen key: uten den gjenbruker React noden, inngangen er alt spilt,
           og neste markoer bytter bare teksten uten bevegelse. */
        <div
          className={`toast${toastInne ? " toast--in" : ""}`}
          key={sisteToast.current.id}
        >
          <span className="mono">{sisteToast.current.text}</span>
          <button type="button" onClick={undo}>Angre</button>
        </div>
      ) : null}
    </div>
  );
});
